import { z } from 'zod';

// 大会のルール（tournaments.rules）。spec.md「ルール設定」に、
// デザイン要件定義書の詳細設定（着順が尽きたとき・チーム戦で押せなくなる範囲）を足したもの。
export const PresetSchema = z.enum(['easy', '7o3x', 'updown', 'rest']);
export type Preset = z.infer<typeof PresetSchema>;

export const SpeedSchema = z.union([z.literal(0.8), z.literal(1), z.literal(1.2)]);
export type Speed = z.infer<typeof SpeedSchema>;

export const RulesSchema = z.object({
  preset: PresetSchema,
  onWrong: z.enum(['reset', 'keepOrder']),
  /** 着順保持で着順が尽きたとき：続きから読む／正解発表 */
  onQueueEmpty: z.enum(['resume', 'reveal']),
  /**
   * 誤答・一時停止のあと読み直すとき：最初から（既定）／止めた所の続きから。
   * 音声ファイルは途中から流すと言葉の途中から始まることがあるので、最初から読み直すのを既定にする
   */
  rereadFrom: z.enum(['start', 'continue']).default('start'),
  /** チーム戦で誤答したとき、その問題で押せなくなる範囲 */
  teamLockScope: z.enum(['team', 'self']),
  penalty: z.enum(['none', 'minus', 'rest', 'zero']),
  minusPoints: z.number().int().min(1).max(10),
  restQuestions: z.number().int().min(1).max(5),
  answerTimeSec: z.number().int().min(1).max(60),
  thinkingTimeSec: z.number().int().min(0).max(60),
  pointsCorrect: z.number().int().min(1).max(10),
  winAtCorrect: z.number().int().min(1).max(100).nullable(),
  loseAtWrong: z.number().int().min(1).max(100).nullable(),
  speed: SpeedSchema,
  judgeWindowMs: z.number().int().min(50).max(1000),
});
export type Rules = z.infer<typeof RulesSchema>;

const base: Rules = {
  preset: 'easy',
  onWrong: 'reset',
  onQueueEmpty: 'resume',
  rereadFrom: 'start',
  teamLockScope: 'team',
  penalty: 'none',
  minusPoints: 1,
  restQuestions: 1,
  answerTimeSec: 5,
  thinkingTimeSec: 5,
  pointsCorrect: 1,
  winAtCorrect: null,
  loseAtWrong: null,
  speed: 1,
  judgeWindowMs: 150,
};

export const PRESETS: Record<Preset, { label: string; summary: string; rules: Rules }> = {
  easy: { label: 'かんたん', summary: '正解＋1点。誤答しても減点なし', rules: { ...base, preset: 'easy' } },
  '7o3x': {
    label: '7○3×',
    summary: '7問正解で勝ち抜け、3回誤答で失格',
    rules: { ...base, preset: '7o3x', winAtCorrect: 7, loseAtWrong: 3 },
  },
  updown: {
    label: 'アップダウン',
    summary: '誤答すると0点に戻る',
    rules: { ...base, preset: 'updown', penalty: 'zero' },
  },
  rest: {
    label: '休み',
    summary: '誤答すると次の問題はお休み',
    rules: { ...base, preset: 'rest', penalty: 'rest', restQuestions: 1 },
  },
};

export const PRESET_ORDER: Preset[] = ['easy', '7o3x', 'updown', 'rest'];

export function presetRules(preset: Preset): Rules {
  return { ...PRESETS[preset].rules };
}

export type Format = 'individual' | 'team';

export type RuleLine = { mark: '1' | '2' | '○' | '×' | '★'; text: string };

/** ルール説明（H-03 のプレビューと P-06 にそのまま出す文） */
export function describeRules(
  rules: Rules,
  opts: { format: Format; questionCount: number },
): { lines: RuleLine[]; footer: string } {
  const who = opts.format === 'team' ? 'チーム' : '人';
  const lines: RuleLine[] = [
    { mark: '1', text: '読まれている途中でも、答えが分かったらスマホをタップ' },
    { mark: '2', text: `いちばん早く押した人が、声に出して答えます（${rules.answerTimeSec}秒以内）` },
    { mark: '○', text: `正解すると＋${rules.pointsCorrect}点` },
  ];

  const penalty = {
    none: '間違えても減点なし',
    minus: `間違えると−${rules.minusPoints}点`,
    zero: '間違えると0点に戻ります',
    rest: `間違えると次の${rules.restQuestions === 1 ? '問題' : `${rules.restQuestions}問`}はお休み`,
  }[rules.penalty];
  const reread = rules.rereadFrom === 'continue' ? '続きから' : '最初から';
  const after =
    rules.onWrong === 'reset'
      ? `その問題はお休みで、読み上げは${reread}`
      : `その問題はお休みで、次に押した人が答えます（押した人が全員間違えたら、${rules.onQueueEmpty === 'resume' ? `読み上げは${reread}` : '正解を発表'}）`;
  lines.push({ mark: '×', text: `${penalty}。${after}` });

  if (rules.winAtCorrect !== null || rules.loseAtWrong !== null) {
    const parts: string[] = [];
    if (rules.winAtCorrect !== null) {
      parts.push(
        rules.penalty === 'zero'
          ? `${rules.winAtCorrect * rules.pointsCorrect}点で勝ち抜け`
          : `${rules.winAtCorrect}問正解で勝ち抜け`,
      );
    }
    if (rules.loseAtWrong !== null) parts.push(`${rules.loseAtWrong}回間違えると失格`);
    lines.push({ mark: '★', text: parts.join('、') });
  }

  const footer =
    rules.winAtCorrect !== null
      ? `全${opts.questionCount}問。先に勝ち抜けた${who}から順位が決まります`
      : `全${opts.questionCount}問。最後にいちばん得点が多い${who}が優勝です`;
  return { lines, footer };
}

/** 進行コンソールの下に出す短い説明。例：かんたん（正解＋1点・誤答ペナルティなし・リセット方式） */
export function rulesSummary(rules: Rules): string {
  const penalty = {
    none: '誤答ペナルティなし',
    minus: `誤答−${rules.minusPoints}点`,
    zero: '誤答で0点',
    rest: `誤答で${rules.restQuestions}問休み`,
  }[rules.penalty];
  const parts = [`正解＋${rules.pointsCorrect}点`, penalty, rules.onWrong === 'reset' ? 'リセット方式' : '着順保持'];
  if (rules.winAtCorrect !== null) parts.push(`${rules.winAtCorrect}問正解で勝ち抜け`);
  if (rules.loseAtWrong !== null) parts.push(`${rules.loseAtWrong}回誤答で失格`);
  return `${PRESETS[rules.preset].label}（${parts.join('・')}）`;
}
